import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiLock, FiEye, FiEyeOff, FiLogIn, FiUserPlus, FiArrowLeft, FiMail, FiCheckCircle } = FiIcons;

const Login = () => {
  const [viewState, setViewState] = useState('login'); // 'login', 'signup', 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { login, signup, resetPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let result;
      if (viewState === 'login') {
        result = await login(email, password);
      } else if (viewState === 'signup') {
        result = await signup(email, password, { name: fullName });
      } else if (viewState === 'forgot') {
        if (!email) {
          setError("Please enter your email address.");
          setLoading(false);
          return;
        }
        result = await resetPassword(email);
        if (result.success) {
          setSuccess('Password reset link sent! Please check your email inbox.');
          setTimeout(() => {
            // Optional: Auto switch back to login after a few seconds
            // setViewState('login');
          }, 3000);
        }
      }

      if (result && !result.success) {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchView = (view) => {
    setViewState(view);
    setError('');
    setSuccess('');
    // Keep email if switching between login/forgot
    if (view === 'login' || view === 'forgot') {
        // email stays
    } else {
        // clear for signup
        if (view === 'signup') setEmail('');
    }
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md relative overflow-hidden"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <SafeIcon icon={viewState === 'forgot' ? FiMail : FiUser} className="w-8 h-8 text-white" />
          </motion.div>
          <h1 className="text-2xl font-bold text-gray-900">
            {viewState === 'login' ? 'Welcome Back' 
             : viewState === 'signup' ? 'Create Account' 
             : 'Reset Password'}
          </h1>
          <p className="text-gray-600 mt-2">
            {viewState === 'login' ? 'Sign in to access your CRM' 
             : viewState === 'signup' ? 'Get started with your free account'
             : 'Enter your email to receive reset instructions'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <AnimatePresence mode="wait">
            {viewState === 'signup' && (
              <motion.div
                key="fullname"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all"
                    placeholder="John Doe"
                    required={viewState === 'signup'}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <div className="relative">
              <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="email@company.com"
                required
              />
            </div>
          </div>

          {viewState !== 'forgot' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all"
                  placeholder="••••••••"
                  required={viewState !== 'forgot'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {viewState === 'login' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => switchView('forgot')}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                Forgot Password?
              </button>
            </div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-lg border border-red-100"
            >
              {error}
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-green-600 text-sm text-center bg-green-50 p-3 rounded-lg border border-green-100 flex items-center justify-center"
            >
              <SafeIcon icon={FiCheckCircle} className="w-4 h-4 mr-2" />
              {success}
            </motion.div>
          )}

          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {loading ? (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
              />
            ) : (
              <>
                <SafeIcon 
                  icon={viewState === 'login' ? FiLogIn : viewState === 'signup' ? FiUserPlus : FiMail} 
                  className="w-5 h-5" 
                />
                <span>
                  {viewState === 'login' ? 'Sign In' 
                   : viewState === 'signup' ? 'Create Account' 
                   : 'Send Reset Link'}
                </span>
              </>
            )}
          </motion.button>
        </form>

        <div className="mt-6 text-center border-t border-gray-100 pt-4">
          {viewState === 'forgot' ? (
            <button
              onClick={() => switchView('login')}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center w-full"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
              Back to Sign In
            </button>
          ) : (
            <p className="text-sm text-gray-600">
              {viewState === 'login' ? "Don't have an account?" : "Already have an account?"}
              <button
                onClick={() => switchView(viewState === 'login' ? 'signup' : 'login')}
                className="ml-1 font-semibold text-blue-600 hover:text-blue-700"
              >
                {viewState === 'login' ? 'Sign Up' : 'Log In'}
              </button>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Login;